import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import ReactDOMServer from "react-dom/server";
import PropTypes from "prop-types";

const ROOT_SELECTOR_PLACEHOLDER = "__ROOT__";
const StyleContext = createContext();

/**
 * @returns string
 */
const generateRandomId = () => {
  if (process.env.NODE_ENV === "test") {
    return `wysiwyg-styles-TEST`;
  }

  const randomId = Date.now() + "-" + Math.round(Math.random() * 10000);
  return `wysiwyg-styles-${randomId}`;
};

/**
 * @type {React.FC}
 */
const StyleProvider = ({ children, ...props }) => {
  const [wrapperId, setWrapperId] = useState(generateRandomId);

  useEffect(() => {
    // We regenerate a new id after rehydration so that
    // the component has a fixed and known id during client navigation.
    // It is simpler than trying to read the one generated during SSR.
    setWrapperId(generateRandomId());
  }, []);

  const scopeStyle = useCallback(
    (style) =>
      style.replace(
        new RegExp(ROOT_SELECTOR_PLACEHOLDER, "g"),
        `#${wrapperId}`
      ),
    [wrapperId]
  );

  return (
    <StyleContext.Provider value={scopeStyle}>
      <div {...props} id={wrapperId}>
        {children}
      </div>
    </StyleContext.Provider>
  );
};

/**
 * @type {React.FC<StyleProps>}
 */
const Style = ({ children, rootSelector }) => {
  const scopeStyle = useContext(StyleContext);

  const style = useMemo(() => {
    const rawStyles = ReactDOMServer.renderToString(children);
    if (!rootSelector) {
      return rawStyles;
    }
    return rawStyles.replace(
      new RegExp(rootSelector, "g"),
      ROOT_SELECTOR_PLACEHOLDER
    );
  }, [children, rootSelector]);

  return <style>{scopeStyle(style)}</style>;
};

/**
 * @typedef StyleProps
 * @property {string} children
 * @property {string} [rootSelector=""]
 */
Style.propTypes = {
  children: PropTypes.node,
  rootSelector: PropTypes.string,
};

export { StyleProvider };
export default Style;
